import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Section from '@/components/Section/section';
import { color } from '@/themes/color';
import { typography } from '@/themes/typography';
import heroImage from '@/assets/hero-image.webp';
import CustomButton from '@/components/Button/button';
interface HeroSectionProps {
  onDownloadClick: () => void;
}

export default function HeroSection({
  onDownloadClick,
}: HeroSectionProps): JSX.Element {
  return (
    <Section
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        minHeight: { xs: '100%', md: '88vh' },
        height: { xs: '100%', md: '88vh' },
        minWidth: 0,
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          flex: { xs: '0 0 auto', md: 1 },
          width: '100%',
          minWidth: 0,
          minHeight: { xs: 'min(38vh, 280px)', md: 0 },
          height: { xs: 'min(38vh, 280px)', md: '100%' },
        }}
      >
        <Box
          component="img"
          src={heroImage}
          alt="English and Welsh vineyard landscape with wine bottles"
          fetchPriority="high"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      </Box>
      <Box
        sx={{
          flex: { xs: '1 1 auto', md: 1 },
          width: '100%',
          minWidth: 0,
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: { xs: 'flex-start', md: 'center' },
          padding: {
            xs: '32px 24px 48px',
            sm: '40px 28px 56px',
            md: '64px 32px',
          },
          backgroundColor: color.cream['100'],
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
          <Typography
            component="h1"
            sx={{
              ...typography.display,
              display: 'block',
              color: color.charcoal.default,
              marginBottom: '32px',
              overflowWrap: 'break-word',
              fontSize: {
                xs: 'var(--font-size-36, 36px)',
                sm: 'var(--font-size-48, 48px)',
                md: typography.display.fontSize,
              },
            }}
          >
            The definitive guide to exploring UK vineyards
          </Typography>
          <Typography
            sx={{
              ...typography.heading4,
              display: 'block',
              color: color.charcoal.default,
              fontWeight: 350,
              marginBottom: '16px',
              maxWidth: { xs: '100%', md: 600 },
              overflowWrap: 'break-word',
              fontSize: {
                xs: 'var(--font-size-18, 18px)',
                sm: 'var(--font-size-20, 20px)',
                md: typography.heading4.fontSize,
              },
            }}
          >
            Discover the people, places and stories behind English and Welsh
            wine.
          </Typography>
          <Typography
            sx={{
              ...typography.heading5,
              display: 'block',
              color: color.charcoal.default,
              fontWeight: 325,
              marginBottom: '16px',
              maxWidth: { xs: '100%', md: 600 },
              overflowWrap: 'break-word',
              fontSize: {
                xs: 'var(--font-size-16, 16px)',
                sm: 'var(--font-size-18, 18px)',
                md: typography.heading5.fontSize,
              },
            }}
          >
            UK Vineyard Guide is your go-to digital resource for planning
            vineyard visits and exploring the UK’s growing wine scene.
          </Typography>
        </Box>
        <CustomButton
          variant="contained"
          onClick={() => {
            gtag('event', 'cta_click', {
              event_category: 'Engagement',
              event_label: 'Download App – Hero Section',
            });
            onDownloadClick();
          }}
          sx={{
            mt: '16px',
            alignSelf: { xs: 'stretch', md: 'flex-start' },
            width: { xs: '100%', md: 'auto' },
          }}
        >
          Download App
        </CustomButton>
      </Box>
    </Section>
  );
}
